import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Card,
  Stack,
  Typography,
  FormControlLabel,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  Select,
  Input,
  Grid,
} from "@mui/material";
import {
  defaultMinimumAttachmentSizeKb,
  selectSelectedConnection,
} from "@/features/connections/integrationSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  ShepCheckbox,
  ShepTooltip,
} from "@/components/GenericComponents/ShepComponents";
import { AddIcon, DeleteBinLine, InfoIcon } from "@/ImageRepo";
import axios from "@/features/axiosHelper";
import Dropdown from "@/components/Dropdown/Dropdown";
import ShepButton from "@/components/GenericComponents/ShepButton";
import { createAlert } from "@/features/homepage/alertSliceList";

const EmailSchemaSettings = ({ isGlobal, artifactType }) => {
  const dispatch = useDispatch();
  const selectedConnection = useSelector(selectSelectedConnection);

  const [existingSettings, setExistingSettings] = useState({});
  const [emailAddresses, setEmailAddresses] = useState([]);
  const [contentSource, setContentSource] = useState(-1);
  const [allContentSources, setAllContentSources] = useState([]);
  const [schemaPrompt, setSchemaPrompt] = useState("");
  const [onlyUseLastMessage, setOnlyUseLastMessage] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [connectionSettings, setConnectionSettings] = useState({});
  const [useEndpointPrompt, setUseEndpointPrompt] = useState(true);
  const [useConnectionPrompt, setUseConnectionPrompt] = useState(true);
  const [geminiDetermine, setGeminiDetermine] = useState(false);
  const [autoAccept, setAutoAccept] = useState(false);
  const [autoAcceptThreshold, setAutoAcceptThreshold] = useState(100.0);
  const [basePrompt, setBasePrompt] = useState("");
  const [endpointPrompt, setEndpointPrompt] = useState("");
  const [connectionPrompt, setConnectionPrompt] = useState("");

  const [useGlobalSettings, setUseGlobalSettings] = useState(true);
  const [useGlobalDetection, setUseGlobalDetection] = useState(true);
  const [useGlobalPrompts, setUseGlobalPrompts] = useState(true);
  const [emailSchemaSettings, setEmailSchemaSettings] = useState({});
  const [emailSchemaDetectionSettings, setEmailSchemaDetectionSettings] =
    useState({});
  const [emailSchemaPrompts, setEmailSchemaPrompts] = useState({});

  useEffect(() => {
    GetPrompts();
    GetSources();
    GetExistingSettings();
    GetConnectionSettings();
  }, []);

  useEffect(() => {
    setUseGlobalSettings(existingSettings.UseGlobalSettings);
    setUseGlobalDetection(existingSettings.UseGlobalDetectionSettings);
    setUseGlobalPrompts(existingSettings.UseGlobalPrompts);

    //for "0" to be valid in dropdown rather than falsey

    setEmailSchemaSettings({
      ...(existingSettings?.EmailSchemaSettings ?? {}),
      ContentSource: JSON.stringify(
        existingSettings?.EmailSchemaSettings?.ContentSource ?? 0,
      ),
      MinimumAttachmentSizeKb:
        existingSettings?.EmailSchemaSettings?.MinimumAttachmentSizeKb ??
        defaultMinimumAttachmentSizeKb,
    });
    setEmailSchemaDetectionSettings(
      existingSettings?.EmailSchemaDetectionSettings ?? {},
    );
    setEmailSchemaPrompts(existingSettings?.EmailSchemaPrompts ?? {});
  }, [existingSettings]);

  useEffect(() => {
    setUnsavedChanges(SettingsChanged());
  }, [
    useGlobalSettings,
    useGlobalDetection,
    useGlobalPrompts,
    emailSchemaSettings,
    emailSchemaDetectionSettings,
    emailSchemaPrompts,
  ]);

  const SettingsChanged = () => {
    const detectionKeys = Object.keys(
      existingSettings?.EmailSchemaDetectionSettings ?? {},
    );
    const processingKeys = new Set([
      ...Object.keys(existingSettings?.EmailSchemaSettings ?? {}),
      ...Object.keys(emailSchemaSettings),
    ]);
    const promptKeys = Object.keys(existingSettings?.EmailSchemaPrompts ?? {});

    let changed = false;
    detectionKeys.forEach((key) => {
      if (
        existingSettings.EmailSchemaDetectionSettings[key] !==
        emailSchemaDetectionSettings[key]
      ) {
        changed = true;
      }
    });
    promptKeys.forEach((key) => {
      if (existingSettings.EmailSchemaPrompts[key] !== emailSchemaPrompts[key])
        changed = true;
    });
    //contentsource separated due to stringify needed for the dropdown
    processingKeys.forEach((key) => {
      const defaultValues = {
        MinimumAttachmentSizeKb: defaultMinimumAttachmentSizeKb,
      };
      const existingValue =
        existingSettings?.EmailSchemaSettings?.[key] ?? defaultValues[key];
      const updatedValue = emailSchemaSettings[key];
      if (
        key === "ContentSource" &&
        existingValue != updatedValue
      ) {
        changed = true;
      } else if (
        key !== "ContentSource" &&
        JSON.stringify(existingValue) !== JSON.stringify(updatedValue)
      )
        changed = true;
    });
    return changed;
  };

  const GetConnectionSettings = () => {
    axios
      .get("/connections/email-settings", {
        params: {
          connectionId: selectedConnection.id,
        },
      })
      .then((response) => {
        setConnectionSettings(response.data);
      })
      .catch((err) => {
        dispatch(
          createAlert({
            status: "Error",
            message: "Connection could not be retrieved: " + err.message,
          }),
        );
      });
  };
  const GetSources = () => {
    axios
      .get("/connectionartifactschema/email-sources")
      .then((response) => {
        setAllContentSources(
          Object.entries(response.data)?.map(([key, value]) => {
            return { key: key, label: value };
          }),
        );
      })
      .catch((err) => {
        console.error(err.Message);
        dispatch(
          createAlert({
            status: "Error",
            message: "Error fetching options: " + err.Message,
          }),
        );
      });
  };
  const GetExistingSettings = () => {
    axios
      .get("/connectionartifactschema/email-settings", {
        params: {
          connectionId: selectedConnection.id,
          artifactType: isGlobal ? -1 : artifactType,
        },
      })
      .then((response) => {
        setExistingSettings(response.data);
      })
      .catch((err) => {
        dispatch(
          createAlert({
            status: "Error",
            message: "Connection could not be retrieved: " + err.message,
          }),
        );
      });
  };

  const GetPrompts = () => {
    axios
      .get("/connections/prompts", {
        params: {
          connectionId: selectedConnection.id,
        },
      })
      .then((response) => {
        setBasePrompt(response.data.BasePrompt);
        setEndpointPrompt(response.data.EndpointPrompt);
        setConnectionPrompt(response.data.ConnectionPrompt);
      })
      .catch((error) => {
        console.error({ error });
      });
  };

  const handleSave = () => {
    const ArtifactSettings = {
      UseGlobalSettings: isGlobal ? false : useGlobalSettings,
      EmailSchemaSettings: {
        ...emailSchemaSettings,
        ContentSource: JSON.parse(emailSchemaSettings.ContentSource ?? "0"),
      },
      UseGlobalDetectionSettings: useGlobalDetection,
      EmailSchemaDetectionSettings: {
        ...emailSchemaDetectionSettings,
        EmailAddresses: isGlobal
          ? []
          : emailSchemaDetectionSettings.EmailAddresses,
      },
      UseGlobalPrompts: useGlobalPrompts,
      EmailSchemaPrompts: emailSchemaPrompts,
    };
    axios
      .post("/connectionartifactschema/email-settings", ArtifactSettings, {
        params: {
          connectionId: selectedConnection.id,
          artifactType: isGlobal ? -1 : artifactType,
        },
      })
      .then((response) => {
        if (response.data) {
          dispatch(
            createAlert({
              status: "Success",
              alertType: "result",
              title: "Connection Updated!",
              message: "Connection is updated correctly.",
              buttonLabel: "Close",
            }),
          );
          GetExistingSettings();
        } else {
          dispatch(
            createAlert({
              status: "Error",
              alertType: "result",
              title: "Connection Unable to be Updated",
              message: "Connection failed to update.",
              buttonLabel: "Close",
            }),
          );
        }
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        dispatch(
          createAlert({
            status: "Error",
            alertType: "result",
            title: "Connection Unable to be Updated",
            message: "Connection failed to update.",
            buttonLabel: "Close",
          }),
        );
      });
  };

  return (
    <Container>
      <Card className="global-settings-card">
        <Stack>
          <Box>
            <ProcessingSettings
              isGlobal={isGlobal}
              allContentSources={allContentSources}
              setUseGlobalSettings={setUseGlobalSettings}
              useGlobalSettings={useGlobalSettings}
              settings={emailSchemaSettings}
              setSettings={setEmailSchemaSettings}
            />
          </Box>
          <Box sx={{ marginTop: "30px" }}>
            <PromptSection
              basePrompt={basePrompt}
              schemaPrompt={schemaPrompt}
              connectionPrompt={connectionPrompt}
              endpointPrompt={endpointPrompt}
              promptSettings={emailSchemaPrompts}
              setPromptSettings={setEmailSchemaPrompts}
              isGlobal={isGlobal}
              useGlobalPrompts={useGlobalPrompts}
              setUseGlobalPrompts={setUseGlobalPrompts}
            />
          </Box>
          <Box>
            <ArtifactDetectionSettings
              connectionSettings={connectionSettings}
              isGlobal={isGlobal}
              useGlobalDetection={useGlobalDetection}
              setUseGlobalDetection={setUseGlobalDetection}
              detectionSettings={emailSchemaDetectionSettings}
              setDetectionSettings={setEmailSchemaDetectionSettings}
            />
          </Box>
        </Stack>
        <ShepButton onClick={handleSave}>Save</ShepButton>
        {unsavedChanges && (
          <Typography variant="body2" sx={{ color: "var(--error-primary)" }}>
            There are unsaved changes
          </Typography>
        )}
      </Card>
    </Container>
  );
};

const ProcessingSettings = ({
  isGlobal,
  allContentSources,
  setUseGlobalSettings,
  useGlobalSettings,
  settings,
  setSettings,
}) => {
  const setProperty = (property, value) => {
    setSettings({ ...settings, [property]: value });
  };
  const onContentSourceChange = (event) => {
    const type = event.target.value;
    if (type === "dropdown-action") return;
    setProperty("ContentSource", type);
  };

  return (
    <>
      <Grid
        container
        spacing={12}
        rowSpacing={1}
        style={{ marginBottom: "20px", marginTop: "20px" }}
      >
        <Grid item xs={10}>
          <Typography variant="h2">Processing Settings</Typography>
        </Grid>
        <Grid item xs={2}>
          {!isGlobal && (
            <FormControlLabel
              control={
                <ShepCheckbox
                  onClick={() => setUseGlobalSettings(!useGlobalSettings)}
                  checked={useGlobalSettings}
                />
              }
              label="Use Global Settings"
            />
          )}
        </Grid>
        <Grid item xs={10}>
          <Box
            sx={{
              margin: "30px 0px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="subheading1">
              Content Source in Email
            </Typography>
            <Dropdown
              width={300}
              disabled={!isGlobal && useGlobalSettings}
              //label="Content Source"
              onChange={onContentSourceChange}
              value={settings.ContentSource}
              options={allContentSources}
              multiple={false}
            ></Dropdown>
          </Box>
        </Grid>
        <Grid item xs={2}></Grid>
        <Grid item xs={10}>
          <FormControlLabel
            control={
              <ShepCheckbox
                disabled={!isGlobal && useGlobalSettings}
                onClick={() =>
                  setProperty(
                    "OnlyUseLastMessage",
                    !settings.OnlyUseLastMessage,
                  )
                }
                checked={settings.OnlyUseLastMessage}
              />
            }
            label="Only retrieve data from the last message."
          />
        </Grid>
        <Grid item xs={2}></Grid>
        <Grid item xs={10}>
          <Box sx={{ marginTop: "20px" }}>
            <Typography variant="subheading1">
              Minimum Attachment Size (KB)
            </Typography>
            <Typography variant="body2" sx={{ margin: "10px 0px" }}>
              Attachments and embedded files below this size are ignored. Set
              to 0 to include all files.
            </Typography>
            <Input
              disabled={!isGlobal && useGlobalSettings}
              type="number"
              inputProps={{ min: 0, step: 1 }}
              value={settings.MinimumAttachmentSizeKb}
              onChange={(event) => {
                const newVal = event.target.value;
                setProperty("MinimumAttachmentSizeKb", newVal);
              }}
              onBlur={() => {
                //to handle if user deleted number then moved on to next field
                if (settings.MinimumAttachmentSizeKb === "")
                  setProperty("MinimumAttachmentSizeKb", 0);
              }}
              sx={{ width: "150px" }}
            />
          </Box>
        </Grid>
        <Grid item xs={2}></Grid>
        <Grid item xs={10}>
          <Box>
            <FormControlLabel
              control={
                <ShepCheckbox
                  disabled={!isGlobal && useGlobalSettings}
                  onClick={() =>
                    setProperty("AutoAccept", !settings.AutoAccept)
                  }
                  checked={settings.AutoAccept}
                />
              }
              label="Allow automation to auto accept incoming Artifacts over a specified confidence value."
            />
            <Input
              disabled={
                !settings.AutoAccept || (!isGlobal && useGlobalSettings)
              }
              type="number"
              step="any"
              inputProps={{
                max: 100,
                min: 0,
              }}
              value={settings.AutoAcceptThreshold}
              onChange={(event) => {
                const newVal = event.target.value;
                if (newVal > 100.0) setProperty("AutoAcceptThreshold", 100);
                else if (newVal < 0) setProperty("AutoAcceptThreshold", 0);
                else setProperty("AutoAcceptThreshold", newVal);
              }}
              onBlur={() => {
                //to handle if user deleted number then moved on to next field
                if (settings.AutoAcceptThreshold === "")
                  setProperty("AutoAcceptThreshold", 0);
              }}
              sx={{
                paddingLeft: "10px",
                height: "200px",
                maxHeight: "200px",
                width: "450px",
                "&:hover fieldset": {
                  backgroundColor: "#ffffff",
                },
                "&.MuiFormControl-root .MuiInputBase-root": {
                  borderRadius: "5px",
                },
                "&:hover fieldset": {
                  color: "#000000",
                },
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={2}></Grid>
      </Grid>
    </>
  );
};

const ArtifactDetectionSettings = ({
  connectionSettings,
  isGlobal,
  setUseGlobalDetection,
  useGlobalDetection,
  detectionSettings,
  setDetectionSettings,
}) => {
  const setProperty = (property, value) => {
    setDetectionSettings({ ...detectionSettings, [property]: value });
  };
  return (
    <>
      <Grid
        container
        spacing={12}
        rowSpacing={1}
        style={{ marginBottom: "20px", marginTop: "20px" }}
      >
        <Grid item xs={10}>
          <Box
            sx={{
              marginBottom: "30px",
              display: "flex",
              flexDirection: "row",
              justifyContent: "left",
              gap: "20px",
              alignItems: "center",
            }}
          >
            <Typography variant="h2">Artifact Detection Settings</Typography>
            <ShepTooltip title="Specify how to identify the type of Artifact on the incoming email">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <InfoIcon />
              </div>
            </ShepTooltip>
          </Box>
        </Grid>
        <Grid item xs={2}>
          {!isGlobal && (
            <FormControlLabel
              control={
                <ShepCheckbox
                  onClick={() => setUseGlobalDetection(!useGlobalDetection)}
                  checked={useGlobalDetection}
                />
              }
              label="Use Global Settings"
            />
          )}
        </Grid>
        <Grid item xs={10}>
          <FormControlLabel
            control={
              <ShepCheckbox
                disabled={!isGlobal && useGlobalDetection}
                onClick={() =>
                  setProperty(
                    "GeminiDetermined",
                    !detectionSettings.GeminiDetermined,
                  )
                }
                checked={detectionSettings.GeminiDetermined}
              />
            }
            label={
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                Let Gemini Determine Artifact Type
                <ShepTooltip title="Gemini will determine the most suitable Artifact for the incoming document. All other detection settings will be ignored.">
                  <div>
                    <InfoIcon />
                  </div>
                </ShepTooltip>
              </Box>
            }
            sx={{ marginBottom: "50px" }}
          />
        </Grid>
        <Grid item xs={2}></Grid>
        <Grid item xs={10}>
          {!isGlobal && (
            <WhitelistedEmailsSelect
              isDisabled={!isGlobal && useGlobalDetection}
              emails={detectionSettings.EmailAddresses}
              setEmails={(emails) => setProperty("EmailAddresses", emails)}
              connectionEmails={connectionSettings?.EmailAddresses ?? []}
            />
          )}
        </Grid>
        <Grid item xs={2}></Grid>
      </Grid>
    </>
  );
};

const WhitelistedEmailsSelect = ({
  emails,
  setEmails,
  connectionEmails,
  isDisabled,
}) => {
  const [currentText, setCurrentText] = useState("None");

  const handleChange = (event) => {
    setCurrentText(event.target.value);
  };
  const handleAddEmail = () => {
    if (currentText === "") return;
    if (emails.filter((x) => x == currentText).length > 0) return;
    const allEmails = [...emails, currentText];
    setEmails(allEmails);
  };
  const handleRemoveEmail = ({ email }) => {
    const newList = emails.filter((x) => x !== email);
    setEmails(newList);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "left",
          marginBottom: "15px",
        }}
      >
        <Select
          disabled={isDisabled}
          variant="outlined"
          label="Add Email Address"
          value={currentText}
          onChange={handleChange}
          sx={{ width: 400 }}
          MenuProps={{ style: { zIndex: 1900 } }}
        >
          <MenuItem value={"None"} key={-1}>
            {"None"}
          </MenuItem>
          {connectionEmails?.map((email, index) => (
            <MenuItem value={email} key={index}>
              {email}
            </MenuItem>
          ))}
        </Select>
        <IconButton
          disabled={isDisabled}
          onClick={handleAddEmail}
          label={"Add"}
          iconSize={30}
        >
          <AddIcon />
        </IconButton>
      </Box>
      <Card sx={{ maxHeight: "200px", overflowY: "auto" }}>
        {emails?.map((email, index) => {
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              }}
              key={index}
            >
              <Typography variant="body2">{email}</Typography>
              <IconButton
                disabled={isDisabled}
                onClick={() => handleRemoveEmail({ email })}
                label={"Remove"}
                iconSize={30}
              >
                <DeleteBinLine />
              </IconButton>
            </Box>
          );
        })}
      </Card>
    </>
  );
};

const PromptSection = ({
  basePrompt,
  schemaPrompt,
  connectionPrompt,
  endpointPrompt,
  promptSettings,
  setPromptSettings,
  isGlobal,
  useGlobalPrompts,
  setUseGlobalPrompts,
}) => {
  const setProperty = (property, value) => {
    setPromptSettings({ ...promptSettings, [property]: value });
  };

  return (
    <Grid
      container
      spacing={12}
      rowSpacing={1}
      style={{ marginBottom: "20px", marginTop: "20px" }}
    >
      <Grid item xs={10}>
        <Typography variant="h2">Prompts</Typography>
      </Grid>
      <Grid item xs={2}>
        {!isGlobal && (
          <FormControlLabel
            //sx={{ padding: "50% 0px" }}
            control={
              <ShepCheckbox
                onClick={() => setUseGlobalPrompts(!useGlobalPrompts)}
                checked={useGlobalPrompts}
              />
            }
            label="Use Global Settings"
          />
        )}
      </Grid>

      <Grid item xs={10}>
        <Stack>
          <Typography variant="subheading1">Base Prompt</Typography>
          <Box
            className="user-prompt"
            sx={{
              "&> .MuiFormControl-root > .MuiInputBase-root": {
                height: "auto",
              },
              paddingBottom: "40px",
            }}
          >
            <TextField
              value={basePrompt}
              multiline
              readonly
              disabled={!isGlobal && useGlobalPrompts}
              rows={10}
              sx={{
                width: "100%",
                "&:hover fieldset": {
                  backgroundColor: "#ffffff",
                },
                "&.MuiFormControl-root .MuiInputBase-root": {
                  borderRadius: "5px",
                },
                "&:hover fieldset": {
                  color: "#000000",
                },
              }}
            />
          </Box>
        </Stack>
      </Grid>
      <Grid item xs={2}></Grid>

      <Grid item xs={10}>
        <Box>
          <Typography variant="subheading1">Endpoint Prompt</Typography>
          <Box
            className="user-prompt"
            sx={{
              "&> .MuiFormControl-root > .MuiInputBase-root": {
                height: "auto",
              },
              paddingBottom: "40px",
            }}
          >
            <TextField
              value={endpointPrompt}
              multiline
              readonly
              disabled={!isGlobal && useGlobalPrompts}
              rows={10}
              sx={{
                width: "100%",
                "&:hover fieldset": {
                  backgroundColor: "#ffffff",
                },
                "&.MuiFormControl-root .MuiInputBase-root": {
                  borderRadius: "5px",
                },
                "&:hover fieldset": {
                  color: "#000000",
                },
              }}
            />
          </Box>
        </Box>
      </Grid>
      <Grid item xs={2}>
        <FormControlLabel
          sx={{ padding: "50% 0px" }}
          control={
            <ShepCheckbox
              disabled={!isGlobal && useGlobalPrompts}
              onClick={() =>
                setProperty(
                  "UseEndpointPrompt",
                  !promptSettings.UseEndpointPrompt,
                )
              }
              checked={promptSettings.UseEndpointPrompt}
            />
          }
          label="Use Endpoint Prompt"
        />
      </Grid>
      <Grid item xs={10}>
        {!isGlobal && (
          <Box>
            <Typography variant="subheading1">Connection Prompt</Typography>
            <Box
              className="user-prompt"
              sx={{
                "&> .MuiFormControl-root > .MuiInputBase-root": {
                  height: "auto",
                },
                paddingBottom: "40px",
              }}
            >
              <TextField
                value={connectionPrompt}
                multiline
                readonly
                disabled={!isGlobal && useGlobalPrompts}
                rows={10}
                sx={{
                  width: "100%",
                  "&:hover fieldset": {
                    backgroundColor: "#ffffff",
                  },
                  "&.MuiFormControl-root .MuiInputBase-root": {
                    borderRadius: "5px",
                  },
                  "&:hover fieldset": {
                    color: "#000000",
                  },
                }}
              />
            </Box>
          </Box>
        )}
      </Grid>
      <Grid item xs={2}>
        {!isGlobal && (
          <FormControlLabel
            sx={{ padding: "50% 0px" }}
            control={
              <ShepCheckbox
                disabled={!isGlobal && useGlobalPrompts}
                onClick={() =>
                  setProperty(
                    "UseConnectionPrompt",
                    !promptSettings.UseConnectionPrompt,
                  )
                }
                checked={promptSettings.UseConnectionPrompt}
              />
            }
            label="Use Connection Prompt"
          />
        )}
      </Grid>
      <Grid item xs={10}>
        <Typography variant="subheading1">
          {isGlobal ? "Connection" : "Schema"} Prompt
        </Typography>
        <Box
          className="user-prompt"
          sx={{
            "&> .MuiFormControl-root > .MuiInputBase-root": {
              height: "auto",
            },
            paddingBottom: "40px",
          }}
        >
          <TextField
            disabled={!isGlobal && useGlobalPrompts}
            placeholder={"Additional prompt"}
            value={promptSettings.SchemaPrompt}
            onChange={(event) =>
              setProperty("SchemaPrompt", event.target.value)
            }
            rows={10}
            sx={{
              width: "100%",
              "&:hover fieldset": {
                backgroundColor: "#ffffff",
              },
              "&.MuiFormControl-root .MuiInputBase-root": {
                borderRadius: "5px",
              },
              "&:hover fieldset": {
                color: "#000000",
              },
            }}
            multiline
            inputProps={{ maxLength: 2500 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {promptSettings.SchemaPrompt?.length ?? 0}/{2500}
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Grid>
      <Grid item xs={2}></Grid>
    </Grid>
  );
};

export default EmailSchemaSettings;
